/**
 * Pawn rendering utilities for RoadSection components
 *
 * This utility module provides functions for rendering pawns at specific positions
 * within RoadSection components. It handles positioning, scaling, and stacking of
 * multiple pawns within the white center path squares.
 *
 * Key features:
 * - Dynamic scaling based on number of pawns (1-16 pawns per position)
 * - Grid layout for multiple pawns (2x2, 2x3, 3x3, 3x4, 4x4)
 * - Centered positioning within white center path squares
 * - SVG group transforms for positioning and scaling
 * - Animation support for pawn movements (e.g., jump animation when released from base)
 * - One invisible cell-level hitbox per occupied cell, sized from a real CSS-pixel
 *   budget rather than raw SVG user units (see calculateCellHitboxRect)
 */

import React, { type ReactElement, useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import type { Pawn, PawnProps } from '../../../constants/types';
import type { BoardPosition } from '../../../constants/positions';
import type { CenterSquareBounds } from '../constants/center-square-bounds';
import { BOARD_CONFIG, BOARD_LAYOUT } from '../../../config/board-layout';
import GreenPawn from '../../../pawns/basics/GreenPawn';
import RedPawn from '../../../pawns/basics/RedPawn';
import YellowPawn from '../../../pawns/basics/YellowPawn';
import BluePawn from '../../../pawns/basics/BluePawn';
import {
  CLICKABLE_PAWN_ANIMATION,
  createPawnReleaseAnimation,
  createPawnMovePopAnimation,
  createPawnKickAnimation,
  createPawnRollbackSlideAnimation,
  PAWN_HOME_CELEBRATION_ANIMATION,
  PAWN_KICK_DURATION_MS,
  PAWN_ROLLBACK_DURATION_MS
} from '../../../animations/constants';
import { START_POSITIONS } from '../../../../../types/positions';

/**
 * Valid pawn colors
 */
export type PawnColor = 'green' | 'red' | 'yellow' | 'blue';

/**
 * Native pawn dimensions from Pawn component viewBox (0 0 323 473)
 */
const NATIVE_PAWN_WIDTH = 323;
const NATIVE_PAWN_HEIGHT = 473;

/**
 * Maximum number of pawns that can be rendered at a single position
 */
const MAX_PAWNS_PER_POSITION = 16;

/**
 * The four road sections of the board
 */
export type RoadSectionKey = 'top' | 'right' | 'bottom' | 'left';

/**
 * Per-section geometry of the road-section `<svg>`, mirrored from the RoadSection
 * components: Top/Bottom are `viewBox="0 0 268 616"` drawn at scale 0.285,
 * Left/Right are `viewBox="0 0 616 268"` drawn at scale 0.5.
 */
const SECTION_GEOMETRY: Record<
  RoadSectionKey,
  { viewBox: { width: number; height: number }; scale: number }
> = {
  top: { viewBox: { width: 268, height: 616 }, scale: BOARD_LAYOUT.roadSections.top.scale },
  bottom: { viewBox: { width: 268, height: 616 }, scale: BOARD_LAYOUT.roadSections.bottom.scale },
  left: { viewBox: { width: 616, height: 268 }, scale: BOARD_LAYOUT.roadSections.left.scale },
  right: { viewBox: { width: 616, height: 268 }, scale: BOARD_LAYOUT.roadSections.right.scale }
};

/**
 * Reference rendered board width in CSS pixels
 *
 * The board `<svg>` is `w-full h-auto`, so its user-unit-to-CSS-pixel ratio moves
 * with the viewport. 390px is the mobile width story 23-08 measured against — the
 * narrow end of the range that matters.
 */
const REFERENCE_BOARD_WIDTH_CSS_PX = 390;

function cssPxPerSectionUnit(section: RoadSectionKey): number {
  const { viewBox, scale } = SECTION_GEOMETRY[section];
  // A nested `<svg>` with no width/height defaults to 100% x 100% of the board
  // viewport, then `xMidYMid meet` fits its viewBox inside that box.
  const fit = Math.min(
    BOARD_CONFIG.size / viewBox.width,
    BOARD_CONFIG.viewBoxHeight / viewBox.height
  );

  return (
    fit *
    scale *
    BOARD_CONFIG.roadCenterTransform.scale *
    (REFERENCE_BOARD_WIDTH_CSS_PX / BOARD_CONFIG.size)
  );
}

/**
 * SVG user units per CSS pixel inside each road section's own coordinate space.
 *
 * Derived from the full transform chain a section unit travels through:
 *   nested-<svg> preserveAspectRatio fit
 *     x <g transform=getTransform(roadSections.<side>)> scale
 *     x <g transform=getRoadCenterTransform()> scale
 *     x (rendered board CSS px / board viewBox width)
 *
 * This is the measuring tape for CELL_HITBOX_TUNING — it is what lets a value in
 * that table be judged in real CSS pixels. It is not applied to the geometry.
 */
export const SVG_UNITS_PER_CSS_PX: Record<RoadSectionKey, number> = {
  top: 1 / cssPxPerSectionUnit('top'),
  bottom: 1 / cssPxPerSectionUnit('bottom'),
  left: 1 / cssPxPerSectionUnit('left'),
  right: 1 / cssPxPerSectionUnit('right')
};

/**
 * First letter of a BoardPosition -> the section that renders it
 * (G = Green/Bottom, B = Blue/Top, Y = Yellow/Left, R = Red/Right)
 */
const POSITION_PREFIX_TO_SECTION: Record<string, RoadSectionKey> = {
  G: 'bottom',
  B: 'top',
  Y: 'left',
  R: 'right'
};

/**
 * ============================================================================
 * TUNE THE TAP ZONES HERE — the only place cell hitbox geometry is decided.
 * ============================================================================
 *
 * Every value is in that section's own SVG user units. Nothing is derived,
 * clamped, or floored: what you type here is exactly what renders.
 *
 * - `width` / `height` — size of the tap zone.
 * - `offsetX` / `offsetY` — nudge from the cell centre. Positive X moves right,
 *   positive Y moves down. 0 = perfectly centred on the cell.
 *
 * **The four sections do NOT share a cell size** — this is the thing that makes
 * one number look right on one section and wrong on another:
 *
 * | section | cell (w x h)   | pitch to next cell |
 * |---------|----------------|--------------------|
 * | top     | 48.05 x 48.05  | 76.84              |
 * | bottom  | 56.11 x 48.05  | 76.84              |
 * | left    | 56.11 x 48.05  | 76.84              |
 * | right   | 56.11 x 48.05  | 76.84              |
 *
 * So the same number is a different fraction of a cell on `top` than elsewhere.
 *
 * What the numbers mean on a 390px-wide board:
 *
 * | SVG units | CSS px (top/bottom) | CSS px (left/right) |
 * |-----------|---------------------|---------------------|
 * | 48        | 16.1                | 15.9                |
 * | 56        | 18.8                | 18.5                |
 * | 75        | 25.1                | 24.8                |
 * | 80        | 26.8                | 26.5                |
 * | 81        | 27.1                | 26.8  <- pre-story tap target |
 * | 106       | 35.5                | 35.1                |
 * | 131       | 44.0 (WCAG)         | 43.3                |
 *
 * Two soft limits, both advisory — the code renders whatever you set:
 * - Below ~81 units a tap is *smaller* than the pre-story per-pawn hitbox. That
 *   is not automatically worse: there is now one zone per cell that resolves
 *   deterministically, instead of N overlapping ones where only the last-painted
 *   pawn was reachable at all.
 * - Above 76.84 units (the pitch) a zone reaches the next cell's centre, so
 *   adjacent clickable cells start competing for the same tap.
 *
 * A width or height of 0 renders a zone nobody can tap — a test guards against
 * that, but do not ship one.
 *
 * Use `toCssPx(units, 'top' | 'bottom' | 'left' | 'right')` to check a value.
 */
export const CELL_HITBOX_TUNING: Record<
  RoadSectionKey,
  { width: number; height: number; offsetX: number; offsetY: number }
> = {
  // Tuned on-device against Storybook -> Game/Board/RoadSections/MultiPawnTapTarget.
  // top/bottom and left/right currently share values; they are kept as four
  // independent entries so either pair can diverge without a code change.
  top: { width: 75, height: 75, offsetX: -5, offsetY: 35 },
  bottom: { width: 75, height: 75, offsetX: -5, offsetY: 35 },
  right: { width: 80, height: 80, offsetX: 50, offsetY: -20 },
  left: { width: 80, height: 80, offsetX: 50, offsetY: -20 }
};

/**
 * Resolve which road section renders a board position
 *
 * @param position - BoardPosition ID (e.g. 'GC1', 'BS3', 'YFINAL')
 * @returns The section that draws it; defaults to 'bottom' for unknown prefixes
 */
export function getRoadSectionKey(position: BoardPosition): RoadSectionKey {
  return POSITION_PREFIX_TO_SECTION[position.charAt(0)] ?? 'bottom';
}

/**
 * Convert a CSS-pixel length into road-section SVG user units
 *
 * @param cssPx - Length in CSS pixels on a REFERENCE_BOARD_WIDTH_CSS_PX board
 * @param section - Which road section the length belongs to
 * @returns The equivalent length in that section's SVG user units
 */
export function toSvgUnits(cssPx: number, section: RoadSectionKey): number {
  return cssPx * SVG_UNITS_PER_CSS_PX[section];
}

/**
 * Convert a road-section SVG user length into CSS pixels
 *
 * @param svgUnits - Length in the section's SVG user units
 * @param section - Which road section the length belongs to
 * @returns The equivalent length in CSS pixels on a reference-width board
 */
export function toCssPx(svgUnits: number, section: RoadSectionKey): number {
  return svgUnits / SVG_UNITS_PER_CSS_PX[section];
}

/**
 * Scale tier keys for pawn rendering
 */
type ScaleTierKey =
  | 'single'
  | 'double'
  | 'multiple'
  | 'fiveSix'
  | 'sevenNine'
  | 'tenTwelve'
  | 'thirteenSixteen';

/**
 * Unified scale tier configuration
 * Consolidates all scaling parameters for each pawn count tier
 */
interface ScaleTierConfig {
  /** Scale factor for pawn rendering */
  scale: number;
  /** Vertical adjustment factor (negative = shift up) */
  verticalAdj: number;
  /** Grid layout dimensions */
  grid: { cols: number; rows: number };
  /** Spacing factor for grid layout */
  spacing: number;
  /** Compensation factors for horizontal sections (null for single pawn) */
  compensation: { x: number; y: number } | null;
}

/**
 * Consolidated scale tier configuration
 * Single source of truth for all pawn rendering parameters
 */
const SCALE_TIERS: Record<ScaleTierKey, ScaleTierConfig> = {
  single: {
    scale: 0.25,
    verticalAdj: 0,
    grid: { cols: 1, rows: 1 },
    spacing: 0,
    compensation: null
  },
  double: {
    scale: 0.2,
    verticalAdj: -0.15,
    grid: { cols: 2, rows: 1 },
    spacing: 0.3,
    compensation: { x: 0.3, y: 0.4 }
  },
  multiple: {
    scale: 0.16,
    verticalAdj: -0.2,
    grid: { cols: 2, rows: 2 },
    spacing: 0.5,
    compensation: { x: 0.75, y: 0.75 }
  },
  fiveSix: {
    scale: 0.13,
    verticalAdj: -0.3,
    grid: { cols: 2, rows: 3 },
    spacing: 0.55,
    compensation: { x: 0.8, y: 1.0 }
  },
  sevenNine: {
    scale: 0.11,
    verticalAdj: -0.4,
    grid: { cols: 3, rows: 3 },
    spacing: 0.65,
    compensation: { x: 1.6, y: 1.6 }
  },
  tenTwelve: {
    scale: 0.09,
    verticalAdj: -0.6,
    grid: { cols: 3, rows: 4 },
    spacing: 0.75,
    compensation: { x: 2.4, y: 2.4 }
  },
  thirteenSixteen: {
    scale: 0.07,
    verticalAdj: -0.9,
    grid: { cols: 4, rows: 4 },
    spacing: 0.85,
    compensation: { x: 3.5, y: 3.5 }
  }
} as const;

/**
 * Backward-compatible exports for existing code
 * Note: These exports are derived from SCALE_TIERS for maintainability
 */
export const PAWN_SCALES = {
  single: SCALE_TIERS.single.scale,
  double: SCALE_TIERS.double.scale,
  multiple: SCALE_TIERS.multiple.scale,
  fiveSix: SCALE_TIERS.fiveSix.scale,
  sevenNine: SCALE_TIERS.sevenNine.scale,
  tenTwelve: SCALE_TIERS.tenTwelve.scale,
  thirteenSixteen: SCALE_TIERS.thirteenSixteen.scale
} as const;

export const VERTICAL_ADJUSTMENTS = {
  single: SCALE_TIERS.single.verticalAdj,
  double: SCALE_TIERS.double.verticalAdj,
  multiple: SCALE_TIERS.multiple.verticalAdj,
  fiveSix: SCALE_TIERS.fiveSix.verticalAdj,
  sevenNine: SCALE_TIERS.sevenNine.verticalAdj,
  tenTwelve: SCALE_TIERS.tenTwelve.verticalAdj,
  thirteenSixteen: SCALE_TIERS.thirteenSixteen.verticalAdj
} as const;

export const SCALE_COMPENSATION_FACTORS = {
  twoPawns: SCALE_TIERS.double.compensation,
  multiple: SCALE_TIERS.multiple.compensation,
  fiveSix: SCALE_TIERS.fiveSix.compensation,
  sevenNine: SCALE_TIERS.sevenNine.compensation,
  tenTwelve: SCALE_TIERS.tenTwelve.compensation,
  thirteenSixteen: SCALE_TIERS.thirteenSixteen.compensation
} as const;

/**
 * Pawn component lookup by color
 */
const PAWN_COMPONENTS: Record<PawnColor, React.ComponentType<PawnProps>> = {
  green: GreenPawn,
  red: RedPawn,
  yellow: YellowPawn,
  blue: BluePawn
} as const;

/**
 * Get the appropriate pawn component based on pawn color
 *
 * @param color - Pawn color ('green', 'red', 'yellow', 'blue')
 * @param pawnComponents - Optional skin-specific pawn component map (overrides defaults)
 * @returns React component for the specified color pawn
 */
function getPawnComponent(
  color: string,
  pawnComponents?: Partial<Record<PawnColor, React.ComponentType<PawnProps>>>
): React.ComponentType<PawnProps> {
  const key = color as PawnColor;
  return pawnComponents?.[key] ?? PAWN_COMPONENTS[key];
}

/**
 * Get the full scale tier configuration for a given pawn count
 * Returns all rendering parameters in a single lookup
 *
 * @param pawnCount - Number of pawns at the position (1-16)
 * @returns Complete tier configuration with scale, grid, spacing, etc.
 */
function getScaleTier(pawnCount: number): ScaleTierConfig {
  if (pawnCount === 1) return SCALE_TIERS.single;
  if (pawnCount === 2) return SCALE_TIERS.double;
  if (pawnCount <= 4) return SCALE_TIERS.multiple;
  if (pawnCount <= 6) return SCALE_TIERS.fiveSix;
  if (pawnCount <= 9) return SCALE_TIERS.sevenNine;
  if (pawnCount <= 12) return SCALE_TIERS.tenTwelve;
  return SCALE_TIERS.thirteenSixteen;
}

/**
 * Calculate pawn scale based on number of pawns at position
 *
 * @param pawnCount - Number of pawns at the position (1-16)
 * @param scaleMultiplier - Optional multiplier to adjust base scale (default 1.0)
 * @returns Scale factor for pawn rendering
 */
export function getPawnScale(pawnCount: number, scaleMultiplier = 1.0): number {
  return getScaleTier(pawnCount).scale * scaleMultiplier;
}

/**
 * Get grid dimensions for a given pawn count
 *
 * @param pawnCount - Number of pawns at the position (1-16)
 * @returns Object with cols (columns) and rows for the grid layout
 */
export function getGridDimensions(pawnCount: number): { cols: number; rows: number } {
  return getScaleTier(pawnCount).grid;
}

/**
 * Bounding box of a cell-level hitbox, in road-section SVG user units
 */
export interface CellHitboxRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculate the single interactive rect that covers one board cell
 *
 * Nothing is derived and nothing is clamped: the size and nudge are exactly the
 * CELL_HITBOX_TUNING entry for this section. What you type in that table is what
 * renders — edit it and reload.
 *
 * @param bounds - Bounding box of the cell's white center path square
 * @param section - Which road section draws this cell (selects the tuning entry)
 * @returns Hitbox rect in road-section SVG user units
 */
export function calculateCellHitboxRect(
  bounds: CenterSquareBounds,
  section: RoadSectionKey
): CellHitboxRect {
  const { width, height, offsetX, offsetY } = CELL_HITBOX_TUNING[section];

  return {
    x: bounds.x + bounds.width / 2 - width / 2 + offsetX,
    y: bounds.y + bounds.height / 2 - height / 2 + offsetY,
    width,
    height
  };
}

/**
 * Resolve which pawn a tap on a shared cell belongs to
 *
 * `clickablePawnIds` only ever holds the current user's movable pawns
 * (`usePawnMovement` gates it on the turn owner and on an awaited pawn
 * selection), so an opponent sharing the cell can never be picked. Two of the
 * player's *own* pawns on one cell share a position and a dice value, so their
 * legal destination is identical — picking the first is behaviourally
 * indistinguishable from picking either.
 *
 * @param pawnsAtPosition - Pawns occupying the cell, in render order
 * @param clickablePawnIds - IDs the caller considers clickable this turn
 * @returns The pawn a cell tap should act on, or null when the cell is inert
 */
export function resolveCellTargetPawn(
  pawnsAtPosition: Pawn[],
  clickablePawnIds?: string[]
): Pawn | null {
  if (!clickablePawnIds || clickablePawnIds.length === 0) return null;
  return pawnsAtPosition.find((pawn) => clickablePawnIds.includes(pawn.id)) ?? null;
}

/**
 * Calculate offset positions for multiple pawns in a grid layout
 *
 * Grid layouts by pawn count:
 * - 1 pawn: Single centered
 * - 2 pawns: 2x1 horizontal
 * - 3-4 pawns: 2x2 square
 * - 5-6 pawns: 2x3 rectangle (2 columns, 3 rows)
 * - 7-9 pawns: 3x3 square
 * - 10-12 pawns: 3x4 rectangle (3 columns, 4 rows)
 * - 13-16 pawns: 4x4 square
 *
 * Partial rows (for odd pawn counts) are centered horizontally.
 *
 * @param pawnIndex - Index of pawn in array (0-15)
 * @param pawnCount - Total number of pawns (1-16)
 * @param scale - Scale factor for pawns (for visual size)
 * @param baseScale - Optional base scale for spacing calculations (defaults to scale if not provided)
 * @returns Object with offsetX and offsetY for pawn positioning
 */
export function calculatePawnOffset(
  pawnIndex: number,
  pawnCount: number,
  scale: number,
  baseScale?: number
): { offsetX: number; offsetY: number } {
  // Single lookup for all tier parameters
  const tier = getScaleTier(pawnCount);

  // Single pawn: centered, no offset
  if (pawnCount === 1) {
    return { offsetX: 0, offsetY: 0 };
  }

  // Use baseScale for spacing calculations to normalize spacing across sections
  const spacingScale = baseScale ?? scale;
  const scaledWidth = NATIVE_PAWN_WIDTH * spacingScale;

  // Two pawns: horizontal 2x1 grid (special case)
  if (pawnCount === 2) {
    const spacing = scaledWidth * tier.spacing;
    const offsetX = pawnIndex === 0 ? -spacing : spacing;
    return { offsetX, offsetY: 0 };
  }

  // Get grid dimensions from tier config
  const { cols } = tier.grid;

  // Calculate row and column for this pawn
  const row = Math.floor(pawnIndex / cols);
  const col = pawnIndex % cols;

  // Calculate how many pawns are in this row (for centering partial rows)
  const totalRows = Math.ceil(pawnCount / cols);
  const isLastRow = row === totalRows - 1;
  const pawnsInLastRow = pawnCount % cols || cols;
  const pawnsInThisRow = isLastRow ? pawnsInLastRow : cols;

  // Calculate horizontal centering offset for partial rows
  const rowCenteringOffset = (cols - pawnsInThisRow) / 2;

  // Adjust column position for partial rows
  const adjustedCol = isLastRow ? col + rowCenteringOffset : col;

  // Calculate spacing from tier config
  const spacing = scaledWidth * tier.spacing;

  // Calculate offsets centered around origin
  const centerCol = (cols - 1) / 2;
  const centerRow = (totalRows - 1) / 2;

  const offsetX = (adjustedCol - centerCol) * spacing;
  const offsetY = (row - centerRow) * spacing;

  return { offsetX, offsetY };
}

/**
 * Calculate center position of white center path square
 *
 * @param bounds - Bounding box of center path square
 * @returns Object with centerX and centerY coordinates
 */
function calculateSquareCenter(bounds: CenterSquareBounds): { centerX: number; centerY: number } {
  return {
    centerX: bounds.x + bounds.width / 2,
    centerY: bounds.y + bounds.height / 2
  };
}

/**
 * Calculate scale compensation for horizontal sections with scaleMultiplier
 *
 * When a scaleMultiplier is applied (e.g., 1.5 for horizontal sections), pawns are
 * rendered larger but spacing calculations use baseScale to maintain consistency.
 * This function calculates the offset needed to properly center the pawn group.
 *
 * @param pawnCount - Number of pawns at position (1-16)
 * @param scale - Full scale factor (with multiplier applied)
 * @param baseScale - Base scale factor (without multiplier)
 * @param scaleMultiplier - Scale multiplier applied to section
 * @returns Compensation offsets for X and Y axes
 */
export function calculateScaleCompensation(
  pawnCount: number,
  scale: number,
  baseScale: number,
  scaleMultiplier: number
): { x: number; y: number } {
  // No compensation needed for single pawns or when no multiplier is applied
  if (pawnCount === 1 || scaleMultiplier === 1.0) {
    return { x: 0, y: 0 };
  }

  // Get compensation factors from tier config
  const factors = getScaleTier(pawnCount).compensation;
  if (!factors) {
    return { x: 0, y: 0 };
  }

  // Calculate compensation based on scale difference
  const scaleDiff = scale - baseScale;
  return {
    x: scaleDiff * NATIVE_PAWN_WIDTH * factors.x,
    y: scaleDiff * NATIVE_PAWN_HEIGHT * factors.y
  };
}

/**
 * Props for the PawnVisual component
 */
interface PawnVisualProps {
  pawn: Pawn;
  PawnComponent: React.ComponentType<PawnProps>;
  pawnStateProps?: Partial<PawnProps>;
  /**
   * True for EVERY movable pawn in the cell — drives the waiting-for-click
   * bounce. A stacked movable pawn that does not bounce reads as unavailable.
   */
  isClickable: boolean;
  /**
   * True only for the one pawn a tap on this cell resolves to. Several pawns can
   * bounce; exactly one is the target.
   */
  isTapTarget: boolean;
  /** True only when the cell is hovered AND this pawn is the tap target */
  isHovered: boolean;
  isAnimating: boolean;
  isKicking: boolean;
  isRollingBack: boolean;
  isFlipping: boolean;
  /** Whether the board wired a click handler at all (drives the motion path) */
  hasClickHandler: boolean;
}

/**
 * Internal component that renders a pawn's visual + animation layer only.
 *
 * Purely presentational: hit-testing lives on the single cell-level rect painted
 * by PawnsAtPosition, and the hover state lives there too, so this component
 * holds no state of its own.
 */
function PawnVisual({
  pawn,
  PawnComponent,
  pawnStateProps,
  isClickable,
  isTapTarget,
  isHovered,
  isAnimating,
  isKicking,
  isRollingBack,
  isFlipping,
  hasClickHandler
}: PawnVisualProps): ReactElement {
  // Determine animation type.
  // An animating pawn sitting on its OWN colour's start cell has just been
  // released from base (a pawn only ever occupies its own start cell right after
  // release) — it plays the full-flip release pop. Any other animating pawn is a
  // normal board move and plays the subtler move pop.
  const shouldKick = isKicking;
  const shouldSlide = isRollingBack;
  // A just-captured pawn flips in place (same flip as a base release) before it
  // rolls back to base. Takes precedence over the generic move pop.
  const shouldVictimFlip = isFlipping && !isRollingBack && !isKicking;
  const isReleasing = isAnimating && pawn.position === START_POSITIONS[pawn.color];
  const shouldRelease = isReleasing && !isFlipping && !isRollingBack && !isKicking;
  const shouldHop = isAnimating && !isReleasing && !isFlipping && !isRollingBack && !isKicking;
  const shouldBounce = isClickable && !isAnimating && !isFlipping && !isRollingBack && !isKicking;
  // A settled pawn resting on its final home cell plays a one-shot celebration.
  // The stable animation reference means it fires once on arrival (when the pawn
  // re-keys out of the hop) and does not replay on later re-renders.
  const shouldCelebrate =
    pawn.position.endsWith('FINAL') &&
    !shouldKick &&
    !shouldSlide &&
    !shouldHop &&
    !shouldRelease &&
    !shouldVictimFlip &&
    !shouldBounce;

  // Merge hover state with existing pawn state props
  const mergedPawnProps: Partial<PawnProps> = {
    ...pawnStateProps,
    isHovered: isHovered || pawnStateProps?.isHovered
  };

  // Render visual feedback filter for hovered state
  const hoverFilter = isHovered ? 'brightness(1.2)' : undefined;

  // Shared debug/testing hooks so the resolved target is observable from the DOM
  const targetAttributes = {
    'data-testid': `pawn-visual-${pawn.id}`,
    'data-pawn-clickable': String(isClickable),
    'data-pawn-tap-target': String(isTapTarget),
    'data-pawn-hovered': String(isHovered)
  };

  return (
    <>
      {/* Pawn visual layer - never hit-tested; the cell rect owns pointer events */}
      {shouldKick ||
      shouldSlide ||
      shouldVictimFlip ||
      shouldRelease ||
      shouldHop ||
      shouldBounce ||
      shouldCelebrate ||
      hasClickHandler ? (
        <motion.g
          key={
            shouldKick
              ? `${pawn.id}-kick`
              : shouldVictimFlip
                ? `${pawn.id}-flip`
                : shouldSlide || shouldRelease || shouldHop
                  ? `${pawn.id}-${pawn.position}`
                  : shouldCelebrate
                    ? `${pawn.id}-final`
                    : pawn.id
          }
          initial={
            shouldKick
              ? { x: 0, scale: 1, rotate: 0 }
              : shouldSlide
                ? { opacity: 1, scale: 1 }
                : shouldVictimFlip || shouldRelease || shouldHop
                  ? { y: 0, scale: 1 }
                  : shouldCelebrate
                    ? { y: 0, scale: 1 }
                    : undefined
          }
          animate={
            shouldKick
              ? createPawnKickAnimation(PAWN_KICK_DURATION_MS)
              : shouldSlide
                ? createPawnRollbackSlideAnimation(PAWN_ROLLBACK_DURATION_MS)
                : shouldVictimFlip
                  ? createPawnReleaseAnimation()
                  : shouldRelease
                    ? createPawnReleaseAnimation()
                    : shouldHop
                      ? createPawnMovePopAnimation()
                      : shouldBounce
                        ? CLICKABLE_PAWN_ANIMATION
                        : shouldCelebrate
                          ? PAWN_HOME_CELEBRATION_ANIMATION
                          : undefined
          }
          layout={shouldSlide ? true : undefined}
          style={{
            pointerEvents: 'none',
            filter: hoverFilter
          }}
          {...targetAttributes}
        >
          <PawnComponent {...mergedPawnProps} />
        </motion.g>
      ) : (
        <g style={{ pointerEvents: 'none', filter: hoverFilter }} {...targetAttributes}>
          <PawnComponent {...mergedPawnProps} />
        </g>
      )}
    </>
  );
}

/**
 * Props for the PawnsAtPosition component
 */
interface PawnsAtPositionProps {
  position: BoardPosition;
  pawnsAtPosition: Pawn[];
  centerSquareBounds: CenterSquareBounds;
  pawnStateProps?: Partial<PawnProps>;
  scaleMultiplier: number;
  animatingPawnIds?: Set<string>;
  onPawnClick?: (pawnId: string) => void;
  clickablePawnIds?: string[];
  rollbackPawnIds?: Set<string>;
  kickingPawnIds?: Set<string>;
  pawnComponents?: Partial<Record<PawnColor, React.ComponentType<PawnProps>>>;
  flippingPawnIds?: Set<string>;
}

/**
 * Internal component owning one occupied cell: its stacked pawn visuals plus the
 * single interactive rect painted above them.
 *
 * The hover state lives here (not per pawn) so a tap anywhere in the cell drives
 * feedback on the *resolved* pawn only.
 */
function PawnsAtPosition({
  position,
  pawnsAtPosition,
  centerSquareBounds,
  pawnStateProps,
  scaleMultiplier,
  animatingPawnIds,
  onPawnClick,
  clickablePawnIds,
  rollbackPawnIds,
  kickingPawnIds,
  pawnComponents,
  flippingPawnIds
}: PawnsAtPositionProps): ReactElement {
  const [isCellHovered, setIsCellHovered] = useState(false);

  // Single tier lookup for all calculations (optimized)
  const pawnCount = Math.min(pawnsAtPosition.length, MAX_PAWNS_PER_POSITION);
  const tier = getScaleTier(pawnCount);
  const baseScale = tier.scale;
  const scale = baseScale * scaleMultiplier;

  // The one pawn a tap on this cell acts on — null makes the cell inert
  const visiblePawns = pawnsAtPosition.slice(0, MAX_PAWNS_PER_POSITION);
  const targetPawn = resolveCellTargetPawn(visiblePawns, clickablePawnIds);
  const isInteractive = Boolean(onPawnClick && targetPawn);

  const handleCellClick = useCallback(() => {
    if (onPawnClick && targetPawn) {
      onPawnClick(targetPawn.id);
    }
  }, [onPawnClick, targetPawn]);

  const handlePointerEnter = useCallback(() => {
    setIsCellHovered(true);
  }, []);

  const handlePointerLeave = useCallback(() => {
    setIsCellHovered(false);
  }, []);

  // Calculate center position
  const { centerX, centerY } = calculateSquareCenter(centerSquareBounds);

  // Calculate centering offset
  const baseOffsetX = (NATIVE_PAWN_WIDTH * scale) / 2;
  const baseOffsetY = (NATIVE_PAWN_HEIGHT * scale) / 2;
  const verticalAdjustment = NATIVE_PAWN_HEIGHT * scale * tier.verticalAdj;
  const centerOffsetX = baseOffsetX;
  const centerOffsetY = baseOffsetY + verticalAdjustment;

  // Calculate scale compensation for horizontal sections
  let compensation = { x: 0, y: 0 };
  if (pawnCount > 1 && scaleMultiplier !== 1.0 && tier.compensation) {
    const scaleDiff = scale - baseScale;
    compensation = {
      x: scaleDiff * NATIVE_PAWN_WIDTH * tier.compensation.x,
      y: scaleDiff * NATIVE_PAWN_HEIGHT * tier.compensation.y
    };
  }

  // Pre-calculate grid parameters for offset calculation (avoid per-pawn tier lookup)
  const { cols } = tier.grid;
  const scaledWidth = NATIVE_PAWN_WIDTH * baseScale;
  const spacing = scaledWidth * tier.spacing;
  const totalRows = Math.ceil(pawnCount / cols);
  const pawnsInLastRow = pawnCount % cols || cols;

  const hitbox = calculateCellHitboxRect(centerSquareBounds, getRoadSectionKey(position));

  return (
    <g>
      {visiblePawns.map((pawn, index) => {
        // Inline offset calculation (optimized - no function call per pawn)
        let offsetX = 0;
        let offsetY = 0;

        if (pawnCount === 2) {
          offsetX = index === 0 ? -spacing : spacing;
        } else if (pawnCount > 1) {
          const row = Math.floor(index / cols);
          const col = index % cols;
          const isLastRow = row === totalRows - 1;
          const pawnsInThisRow = isLastRow ? pawnsInLastRow : cols;
          const rowCenteringOffset = (cols - pawnsInThisRow) / 2;
          const adjustedCol = isLastRow ? col + rowCenteringOffset : col;
          const centerCol = (cols - 1) / 2;
          const centerRow = (totalRows - 1) / 2;
          offsetX = (adjustedCol - centerCol) * spacing;
          offsetY = (row - centerRow) * spacing;
        }

        const translateX = centerX + offsetX - centerOffsetX + compensation.x;
        const translateY = centerY + offsetY - centerOffsetY - compensation.y;

        // Get the correct pawn component based on pawn's color (skin-aware)
        const PawnComponent = getPawnComponent(pawn.color, pawnComponents);

        // Check if this pawn should be animated
        const isAnimating = animatingPawnIds?.has(pawn.id) ?? false;
        const isKicking = kickingPawnIds?.has(pawn.id) ?? false;
        const isRollingBack = rollbackPawnIds?.has(pawn.id) ?? false;
        const isFlipping = flippingPawnIds?.has(pawn.id) ?? false;

        // The waiting-for-click bounce goes to every movable pawn in the cell —
        // hiding it on the stacked ones makes them look unavailable. Only the
        // hover highlight is reserved for the pawn a tap actually resolves to.
        const isClickable = clickablePawnIds?.includes(pawn.id) ?? false;
        const isTapTarget = targetPawn?.id === pawn.id;

        return (
          <g
            key={pawn.id}
            transform={`translate(${String(translateX)}, ${String(translateY)}) scale(${String(scale)})`}
          >
            <PawnVisual
              pawn={pawn}
              PawnComponent={PawnComponent}
              pawnStateProps={pawnStateProps}
              isClickable={isClickable}
              isTapTarget={isTapTarget}
              isHovered={isTapTarget && isCellHovered}
              isAnimating={isAnimating}
              isKicking={isKicking}
              isRollingBack={isRollingBack}
              isFlipping={isFlipping}
              hasClickHandler={Boolean(onPawnClick)}
            />
          </g>
        );
      })}

      {/* One interactive rect per occupied cell, painted last so it sits above
          every pawn visual. Inert when the cell holds no clickable pawn, so it
          never swallows a tap meant for something else. */}
      <rect
        data-testid={`cell-hitbox-${position}`}
        className="ludoka-cell-hitbox"
        x={hitbox.x}
        y={hitbox.y}
        width={hitbox.width}
        height={hitbox.height}
        fill="transparent"
        style={{
          cursor: isInteractive ? 'pointer' : 'default',
          pointerEvents: isInteractive ? 'all' : 'none'
        }}
        onClick={handleCellClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      />
    </g>
  );
}

/**
 * Render pawns at a specific position within a RoadSection
 *
 * This function filters pawns by position, calculates their layout based on count,
 * and renders them within SVG groups with appropriate transforms. It handles:
 * - Dynamic scaling based on pawn count (1-16)
 * - Grid layout for multiple pawns
 * - Centering within white center path square bounds
 * - Automatic pawn component selection based on pawn color
 * - Pass-through of pawn state props (isActive, isHovered, etc.)
 * - Animation support for newly released pawns
 * - One cell-level hitbox sized in real CSS pixels (see calculateCellHitboxRect)
 *
 * @param position - BoardPosition ID to filter pawns (e.g., 'GC1', 'RS3')
 * @param pawns - Array of all pawns (will be filtered by position)
 * @param centerSquareBounds - Bounding box of white center path square
 * @param pawnStateProps - State props to pass through to Pawn components (optional)
 * @param scaleMultiplier - Optional multiplier to adjust pawn scale (default 1.0, use 1.5 for horizontal sections)
 * @param animatingPawnIds - Set of pawn IDs that should play jump animation (optional)
 * @param onPawnClick - Callback when the cell's resolved pawn is clicked (optional)
 * @param clickablePawnIds - Array of pawn IDs that are clickable (optional)
 * @param rollbackPawnIds - Set of pawn IDs that should play rollback slide animation (optional)
 * @param kickingPawnIds - Set of pawn IDs that should play kick animation (optional)
 * @returns SVG group with positioned pawns, or null if no pawns at position
 *
 * @example
 * ```tsx
 * // In BottomRoadSection.tsx (vertical)
 * {renderPawnsAtPosition(
 *   'GC3',
 *   pawns,
 *   { x: 102.11, y: 132.34, width: 56.11, height: 48.05 }
 * )}
 * // In LeftRoadSection.tsx (horizontal - needs larger scale)
 * {renderPawnsAtPosition(
 *   'YC3',
 *   pawns,
 *   { x: 266.32, y: 112.97, width: 56.11, height: 48.05 },
 *   undefined,
 *   1.5
 * )}
 * // With animation for newly released pawn
 * {renderPawnsAtPosition(
 *   'GS2',
 *   pawns,
 *   bounds,
 *   undefined,
 *   1.0,
 *   new Set(['player1-0']) // Animate pawn player1-0
 * )}
 * ```
 */
export function renderPawnsAtPosition(
  position: BoardPosition,
  pawns: Pawn[] | undefined,
  centerSquareBounds: CenterSquareBounds,
  pawnStateProps?: Partial<PawnProps>,
  scaleMultiplier = 1.0,
  animatingPawnIds?: Set<string>,
  onPawnClick?: (pawnId: string) => void,
  clickablePawnIds?: string[],
  rollbackPawnIds?: Set<string>,
  kickingPawnIds?: Set<string>,
  pawnComponents?: Partial<Record<PawnColor, React.ComponentType<PawnProps>>>,
  flippingPawnIds?: Set<string>
): ReactElement | null {
  if (!pawns || pawns.length === 0) return null;

  // Filter pawns at this specific position
  const pawnsAtPosition = pawns.filter((p) => p.position === position);
  if (pawnsAtPosition.length === 0) return null;

  return (
    <PawnsAtPosition
      key={`pawns-${position}`}
      position={position}
      pawnsAtPosition={pawnsAtPosition}
      centerSquareBounds={centerSquareBounds}
      pawnStateProps={pawnStateProps}
      scaleMultiplier={scaleMultiplier}
      animatingPawnIds={animatingPawnIds}
      onPawnClick={onPawnClick}
      clickablePawnIds={clickablePawnIds}
      rollbackPawnIds={rollbackPawnIds}
      kickingPawnIds={kickingPawnIds}
      pawnComponents={pawnComponents}
      flippingPawnIds={flippingPawnIds}
    />
  );
}

/**
 * Render pawns at multiple positions efficiently
 *
 * This helper function renders pawns for an array of positions, reducing
 * repetitive renderPawnsAtPosition calls in RoadSection components.
 *
 * @param positions - Array of BoardPosition IDs to render in order
 * @param pawns - Array of all pawns (will be filtered per position)
 * @param bounds - Record mapping position IDs to their CenterSquareBounds
 * @param pawnStateProps - State props to pass through to Pawn components (optional)
 * @param scaleMultiplier - Optional multiplier to adjust pawn scale (default 1.0)
 * @param animatingPawnIds - Set of pawn IDs that should play jump animation (optional)
 * @param onPawnClick - Callback when pawn is clicked (optional)
 * @param clickablePawnIds - Array of pawn IDs that are clickable (optional)
 * @param rollbackPawnIds - Set of pawn IDs that should play rollback slide animation (optional)
 * @param kickingPawnIds - Set of pawn IDs that should play kick animation (optional)
 * @returns Array of SVG groups with positioned pawns (nulls filtered out)
 *
 * @example
 * ```tsx
 * const RENDER_ORDER = ['GFINAL', 'GS6', 'GC6', 'GF6', ...];
 * {renderMultiplePositions(RENDER_ORDER, pawns, CENTER_SQUARE_BOUNDS)}
 * ```
 */
export function renderMultiplePositions(
  positions: BoardPosition[],
  pawns: Pawn[] | undefined,
  bounds: Record<string, CenterSquareBounds>,
  pawnStateProps?: Partial<PawnProps>,
  scaleMultiplier = 1.0,
  animatingPawnIds?: Set<string>,
  onPawnClick?: (pawnId: string) => void,
  clickablePawnIds?: string[],
  rollbackPawnIds?: Set<string>,
  kickingPawnIds?: Set<string>,
  pawnComponents?: Partial<Record<PawnColor, React.ComponentType<PawnProps>>>,
  flippingPawnIds?: Set<string>
): ReactElement[] {
  return positions
    .map((pos) =>
      renderPawnsAtPosition(
        pos,
        pawns,
        bounds[pos],
        pawnStateProps,
        scaleMultiplier,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )
    )
    .filter((element): element is ReactElement => element !== null);
}
